
import './HomePage.scss';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Grid, Column, Button, Tile, SkeletonText, Tag, IconButton, Modal, Callout, Tabs, TabList, Tab, TabPanels, TabPanel, InlineLoading, InlineNotification } from '@carbon/react';
import { Add, Launch, Share, TrashCan, View, ChevronRight } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import StartFromPromptModal from '../components/ontology/StartFromPromptModal';
import { StartFromKBModal } from '../components/ontology/StartFromKBModal';
import { ShareOntologyModal } from '../components/ontology/ShareOntologyModal';
import { CreateOntologyPanel } from '../components/ontology/CreateOntologyPanel';
import { ontologyGenerationApi } from '../lib/api/ontology-generation.api';
import { ontologyApi } from '../lib/api/ontology.api';
import { shareApi, type SharedOntologyItem } from '../lib/api/share.api';
import { versionApi, type OntologyVersion } from '../lib/api/version.api';
import { draftLabelFromList } from '@snxt/snxt-onto-share';
import { useAuth } from '../lib/auth/auth-context';
import { useAIConfigs, useLlmOverrideStatus } from '../lib/query/hooks/useAIConfigs';
import { useKnowledgeBases } from '../lib/query/hooks/useKnowledgeBases';
import { outcomeMessage, snapshotIds, useResilientMutation } from '../lib/query/useResilientMutation';
import {
  verifyOntologyImported,
  type ImportOntologyResult,
  type ImportOntologyVars,
} from '../lib/query/verifiers';

/**
 * HomePage Component
 * 
 * Landing page for the Ontology Builder application.
 * Professional layout with card-based actions for IBM Science Next.
 */
/** Ontology item as returned by the backend list endpoint */
interface OntologyListItem {
  id: string;
  name: string;
  description?: string;
  /** Legacy denormalized version on `ontologies` (current draft cache). */
  version?: string;
  /** Latest published version_number, or null if never published. */
  latest_published_version?: string | null;
  /** Whether an editable draft exists. False for an import that only has published rows. */
  has_draft?: boolean;
  /**
   * The published number the current draft was forked from, or null if the
   * ontology has never been published. A draft itself has no number.
   */
  draft_parent_version?: string | null;
  /** Aggregated status from server: 'published' if any published version exists. */
  status?: 'draft' | 'published';
  generation_status?: 'queued' | 'running' | 'completed' | 'failed' | null;
  generation_error_message?: string | null;
  created_at: string;
  updated_at: string;
}

export default function HomePage() {
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [showKBModal, setShowKBModal] = useState(false);
  const [showGenerationBanner, setShowGenerationBanner] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);
  const [ontologies, setOntologies] = useState<OntologyListItem[]>([]);
  const [sharedOntologies, setSharedOntologies] = useState<SharedOntologyItem[]>([]);
  const [isLoadingOntologies, setIsLoadingOntologies] = useState(false);
  const [isLoadingShared, setIsLoadingShared] = useState(false);
  const [ontologiesError, setOntologiesError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [shareTarget, setShareTarget] = useState<{ id: string; name: string } | null>(null);
  // Expand/collapse + lazy-loaded versions per ontology
  const [expandedOntologyId, setExpandedOntologyId] = useState<string | null>(null);
  const [versionsByOntology, setVersionsByOntology] = useState<Record<string, OntologyVersion[]>>({});
  const [versionsLoading, setVersionsLoading] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [createPanelOpen, setCreatePanelOpen] = useState(false);
  const { data: aiConfigs } = useAIConfigs();
  const { data: overrideStatus } = useLlmOverrideStatus();
  const hasNoAIConfig = isAuthenticated && !overrideStatus?.overrideActive && aiConfigs && aiConfigs.length === 0;
  const { data: knowledgeBases } = useKnowledgeBases();
  const hasKnowledgeBases = knowledgeBases && knowledgeBases.length > 0;
  const importMutation = useResilientMutation<ImportOntologyResult, ImportOntologyVars>({
    mutationFn: async (vars) => {
      const res = await ontologyApi.importOntology({ name: vars.name, jsonld: vars.jsonld });
      return {
        id: res.id,
        name: res.name,
        counts: { nodes: res.nodeCount, edges: res.edgeCount },
      };
    },
    verify: verifyOntologyImported,
    // The outcome is reported from the hook's own callbacks, not read off `importMutation` after
    // the await: that object is captured at render time, so its `error` would still be the value
    // from before the attempt and every failure would print "unknown error".
    onError: (err) => window.alert('Import failed: ' + (outcomeMessage(err) ?? 'unknown error')),
  });

  // Fetch ontologies from backend
  const fetchOntologies = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoadingOntologies(true);
    setOntologiesError(null);
    try {
      const data = await ontologyApi.getOntologies();
      setOntologies(data as unknown as OntologyListItem[]);
    } catch (error: any) {
      console.error('Failed to fetch ontologies:', error);
      setOntologiesError(error?.message || 'Failed to load ontologies');
    } finally {
      setIsLoadingOntologies(false);
    }
  }, [isAuthenticated]);

  // Fetch shared ontologies
  // Import an ontology from a JSON-LD file (inverse of the JSON-LD export).
  //
  // An import creates a whole ontology, so a lost response followed by a second attempt leaves
  // two of them. `submit` reads the ontology list back before reporting anything: the import
  // landed if a name we asked for is on an ontology this page had not seen.
  const handleImportOntology = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const defaultName = file.name.replace(/\.(jsonld|json)$/i, '');
    const name = window.prompt('Name for the imported ontology:', defaultName);
    if (name === null) return;
    try {
      const text = await file.text();
      const res = await importMutation.submit({
        name: name || defaultName,
        jsonld: text,
        knownIds: snapshotIds(ontologies as unknown as { id: string }[]),
        snapshotAvailable: !isLoadingOntologies && !ontologiesError,
      });
      if (res) {
        window.alert(
          res.counts
            ? `Imported "${res.name}" (${res.counts.nodes} concepts, ${res.counts.edges} relations).`
            : `Imported "${res.name}". The response was lost on the way back, so the concept and relation counts are not known here; open the ontology to see it.`,
        );
      }
      await fetchOntologies();
    } catch {
      // error already surfaced via importMutation.onError
    }
  };

  const fetchSharedOntologies = useCallback(async () => {
    if (!isAuthenticated) return;
    setIsLoadingShared(true);
    try {
      const data = await shareApi.getSharedWithMe();
      setSharedOntologies(data);
    } catch (error: any) {
      console.error('Failed to fetch shared ontologies:', error);
    } finally {
      setIsLoadingShared(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchOntologies();
    fetchSharedOntologies();
  }, [fetchOntologies, fetchSharedOntologies]);

  const hasGeneratingOntologies = useMemo(
    () => ontologies.some((ontology) => ontology.generation_status === 'queued' || ontology.generation_status === 'running'),
    [ontologies]
  );

  useEffect(() => {
    if (!hasGeneratingOntologies) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void fetchOntologies();
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchOntologies, hasGeneratingOntologies]);

  const handleGenerateFromPrompt = async (domain: string) => {
    try {
      const result = await ontologyGenerationApi.generateFromPrompt({ domain });
      
      // Navigate to editor with the generated ontology
      navigate('/editor', {
        state: {
          generatedOntology: result.ontology,
          nodes: result.nodes,
          edges: result.edges
        }
      });
      
      setShowPromptModal(false);
    } catch (error) {
      console.error('Failed to generate ontology:', error);
      throw error;
    }
  };

  const handleDeleteOntology = (id: string, name: string) => {
    setDeleteTarget({ id, name });
  };

  const confirmDeleteOntology = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await ontologyApi.deleteOntology(deleteTarget.id);
      setOntologies((prev) => prev.filter((o) => o.id !== deleteTarget.id));
      setDeleteTarget(null);
    } catch (error) {
      console.error('Failed to delete ontology:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const isOntologyGenerating = (ontology: Pick<OntologyListItem, 'generation_status'>) =>
    ontology.generation_status === 'queued' || ontology.generation_status === 'running';

  const handleOpenOntology = (id: string) => {
    navigate(`/editor/${id}`);
  };

  /** Toggle the version sub-list for an ontology. Lazy-loads on first expand. */
  const toggleVersions = useCallback(
    async (ontologyId: string) => {
      const next = expandedOntologyId === ontologyId ? null : ontologyId;
      setExpandedOntologyId(next);
      if (next && !versionsByOntology[ontologyId] && !versionsLoading[ontologyId]) {
        setVersionsLoading((prev) => ({ ...prev, [ontologyId]: true }));
        try {
          const versions = await versionApi.list(ontologyId);
          setVersionsByOntology((prev) => ({ ...prev, [ontologyId]: versions }));
        } catch (error) {
          console.error('Failed to fetch versions for', ontologyId, error);
        } finally {
          setVersionsLoading((prev) => ({ ...prev, [ontologyId]: false }));
        }
      }
    },
    [expandedOntologyId, versionsByOntology, versionsLoading]
  );

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Grid fullWidth style={{ padding: '2rem 0' }}>
      <Column lg={16} md={8} sm={4}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
          <div>
            <h1 style={{ fontSize: '1.75rem', fontWeight: 600, marginBottom: '0.25rem' }}>Ontology Builder</h1>
            <p style={{ color: 'var(--cds-text-secondary)' }}>
              Build, version, and share domain ontologies with AI assistance.
            </p>
          </div>
          <Button
            kind="primary"
            renderIcon={Add}
            onClick={() => setCreatePanelOpen(true)}
          >
            New Ontology
          </Button>
        </div>

        {/* AI Configuration Warning */}
        {hasNoAIConfig && (
          /*
           * Callout, NOT ActionableNotification, and that swap IS the fix.
           *
           * Measured on the deployed app 2026-08-19: with this warning on screen, clicking the "Domain
           * Description" field and typing produced NOTHING. document.activeElement stayed on
           * `.cds--actionable-notification__action-button` after the click and after every keystroke, so
           * "Generate Ontology" never enabled and the whole "Generate with AI" path was dead. Carbon
           * gives ActionableNotification `role="alertdialog"` and renders focus SENTINELS around it: it
           * is a modal alert by design, and it holds the keyboard until it is answered. With
           * hideCloseButton there was nothing to answer, so the trap was permanent for any account
           * without a provider — and the one action the message recommended was the one thing the user
           * could not reach by typing. Setting hasFocus={false} does not help (tried, still trapped) and
           * Carbon deprecates it, saying in as many words: use Callout instead.
           *
           * Callout is Carbon's component for a persistent, non-blocking page message. Same title,
           * subtitle and action, no alertdialog role, no sentinels, no trap.
           */
          <Callout
            kind="warning"
            lowContrast
            title="No AI provider configured –"
            subtitle="AI-powered features (Generate with AI, chat assistant) require an AI provider to be configured."
            actionButtonLabel="Configure AI Provider"
            onActionButtonClick={() => navigate('/settings')}
            style={{ marginBottom: '1.5rem', maxWidth: '100%', width: '100%' }}
          />
        )}

        {/* Hidden file input for JSON-LD import — owned by the page, triggered by CreateOntologyPanel */}
        <input
          ref={importInputRef}
          type="file"
          accept=".jsonld,.json,application/ld+json,application/json"
          style={{ display: 'none' }}
          onChange={handleImportOntology}
        />

        {/* Start from Prompt Modal */}
        <StartFromPromptModal
          open={showPromptModal}
          onClose={() => setShowPromptModal(false)}
          onGenerate={handleGenerateFromPrompt}
        />

        {/* Start from Knowledge Base Modal */}
        <StartFromKBModal
          open={showKBModal}
          onClose={() => setShowKBModal(false)}
          onGenerationStarted={() => {
            setShowGenerationBanner(true);
            void fetchOntologies();
          }}
        />


        {/* Generation launched banner */}
        {showGenerationBanner && (
          <InlineNotification
            kind="info"
            title="Generation launched —"
            subtitle='The ontology is being generated in the background and may take a few minutes. It will appear below with a "Generating" status once it starts.'
            onCloseButtonClick={() => setShowGenerationBanner(false)}
            lowContrast
            style={{ marginBottom: '1.5rem', maxWidth: '100%', width: '100%' }}
          />
        )}

        {/* Ontologies Section with Tabs */}
        <Tile>
            {!isAuthenticated ? (
              <p className="my-ontologies__signin">Sign in to view your saved ontologies</p>
            ) : (
              <Tabs>
                <TabList aria-label="Ontology tabs">
                  <Tab>My Ontologies</Tab>
                  <Tab>Shared with me{sharedOntologies.length > 0 ? ` (${sharedOntologies.length})` : ''}</Tab>
                </TabList>
                <TabPanels>
                  {/* My Ontologies Tab */}
                  <TabPanel>
                    <div className="my-ontologies__content">
                      {isLoadingOntologies ? (
                        <div className="my-ontologies__loading">
                          <SkeletonText heading width="60%" />
                          <SkeletonText paragraph lineCount={2} width="80%" />
                        </div>
                      ) : ontologiesError ? (
                        <div className="my-ontologies__error">
                          <p>{ontologiesError}</p>
                          <Button kind="ghost" size="sm" onClick={fetchOntologies}>
                            Retry
                          </Button>
                        </div>
                      ) : ontologies.length === 0 ? (
                        <p className="my-ontologies__empty">No ontologies yet. Create your first one to get started!</p>
                      ) : (
                        <div className="my-ontologies__list">
                          {ontologies.map((ontology) => {
                            const isExpanded = expandedOntologyId === ontology.id;
                            const versions = versionsByOntology[ontology.id];
                            const isLoadingVersions = versionsLoading[ontology.id];
                            return (
                              <div key={ontology.id} className={`ontology-group${isExpanded ? ' ontology-group--expanded' : ''}`}>
                                {/* Main ontology row */}
                                <div
                                  className="ontology-item"
                                  onClick={() => {
                                    if (isOntologyGenerating(ontology)) {
                                      return;
                                    }
                                    handleOpenOntology(ontology.id);
                                  }}
                                >
                                  {/* Expand/collapse chevron */}
                                  <button
                                    className={`ontology-item__chevron${isExpanded ? ' ontology-item__chevron--open' : ''}`}
                                    onClick={(e) => { e.stopPropagation(); toggleVersions(ontology.id); }}
                                    aria-label={isExpanded ? 'Collapse versions' : 'Expand versions'}
                                    title={isExpanded ? 'Collapse versions' : 'Show version history'}
                                  >
                                    <ChevronRight />
                                  </button>
                                  <div className="ontology-item__info">
                                    <div className="ontology-item__header">
                                      <h4 className="ontology-item__name">{ontology.name}</h4>
                                      {/* The draft (what opens on click) is named by
                                          its lineage, never by a number it does not
                                          have. Absent for an ontology seeded with
                                          published rows only. */}
                                      {ontology.has_draft && (
                                        <Tag size="sm" type="outline">
                                          {ontology.draft_parent_version
                                            ? `Draft (based on v${ontology.draft_parent_version})`
                                            : 'Draft (unpublished)'}
                                        </Tag>
                                      )}
                                      {ontology.generation_status && ontology.generation_status !== 'completed' && (
                                        <Tag size="sm" type={ontology.generation_status === 'failed' ? 'red' : 'blue'}>
                                          {ontology.generation_status === 'failed' ? 'Generation failed' : 'Generating'}
                                        </Tag>
                                      )}
                                      {/* If a published version exists, show it as secondary context */}
                                      {ontology.latest_published_version && (
                                        <Tag size="sm" type="green">v{ontology.latest_published_version} published</Tag>
                                      )}
                                    </div>
                                    {ontology.description && (
                                      <p className="ontology-item__description">{ontology.description}</p>
                                    )}
                                    <p className="ontology-item__date">
                                      Created {formatDate(ontology.created_at)}
                                    </p>
                                  </div>
                                  {/* No row-level actions — actions live on each version row below */}
                                </div>
                                {/* Expandable version sub-list */}
                                {isExpanded && (
                                  <div className="ontology-versions">
                                    {isLoadingVersions ? (
                                      <InlineLoading description="Loading versions…" />
                                    ) : versions && versions.length > 0 ? (
                                      <ul className="ontology-versions__list">
                                        {versions.map((v) => (
                                          <li
                                            key={v.id}
                                            className="ontology-versions__item"
                                            onClick={() => {
                                              if (isOntologyGenerating(ontology)) {
                                                return;
                                              }
                                              if (v.status === 'draft') navigate(`/editor/${ontology.id}`);
                                              else navigate(`/editor/${ontology.id}?version=${v.id}`);
                                            }}
                                          >
                                            <div className="ontology-versions__info">
                                              <Tag size="sm" type={v.status === 'published' ? 'green' : 'gray'}>
                                                {draftLabelFromList(v, versions)}
                                              </Tag>
                                              <span className="ontology-versions__status">
                                                {v.status === 'published' ? 'Published' : 'Draft'}
                                              </span>
                                              {v.published_at && (
                                                <span className="ontology-versions__date">
                                                  {formatDate(v.published_at)}
                                                </span>
                                              )}
                                              {v.release_notes && (
                                                <span className="ontology-versions__desc">{v.release_notes}</span>
                                              )}
                                            </div>
                                            {/* Per-version action buttons */}
                                            <div className="ontology-versions__actions" onClick={(e) => e.stopPropagation()}>
                                              <IconButton
                                                size="sm"
                                                kind="ghost"
                                                label={
                                                  isOntologyGenerating(ontology)
                                                    ? 'Ontology is still generating'
                                                    : v.status === 'draft'
                                                      ? 'Open draft in editor'
                                                      : 'View this version'
                                                }
                                                align="top"
                                                disabled={isOntologyGenerating(ontology)}
                                                onClick={(e: React.MouseEvent) => {
                                                  e.stopPropagation();
                                                  if (isOntologyGenerating(ontology)) {
                                                    return;
                                                  }
                                                  if (v.status === 'draft') navigate(`/editor/${ontology.id}`);
                                                  else navigate(`/editor/${ontology.id}?version=${v.id}`);
                                                }}
                                              >
                                                {v.status === 'draft' ? <Launch /> : <View />}
                                              </IconButton>
                                              <IconButton
                                                size="sm"
                                                kind="ghost"
                                                label="Share"
                                                align="top"
                                                onClick={(e: React.MouseEvent) => {
                                                  e.stopPropagation();
                                                  setShareTarget({ id: ontology.id, name: ontology.name });
                                                }}
                                              >
                                                <Share />
                                              </IconButton>
                                              {/* Delete only allowed on drafts — published versions are immutable */}
                                              {v.status === 'draft' && (
                                                <IconButton
                                                  size="sm"
                                                  kind="ghost"
                                                  label="Delete ontology"
                                                  align="top-right"
                                                  onClick={(e: React.MouseEvent) => {
                                                    e.stopPropagation();
                                                    handleDeleteOntology(ontology.id, ontology.name);
                                                  }}
                                                >
                                                  <TrashCan />
                                                </IconButton>
                                              )}
                                            </div>
                                          </li>
                                        ))}
                                      </ul>
                                    ) : (
                                      <p className="ontology-versions__empty">No version history yet.</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </TabPanel>

                  {/* Shared with me Tab */}
                  <TabPanel>
                    <div className="my-ontologies__content">
                      {isLoadingShared ? (
                        <div className="my-ontologies__loading">
                          <SkeletonText heading width="60%" />
                          <SkeletonText paragraph lineCount={2} width="80%" />
                        </div>
                      ) : sharedOntologies.length === 0 ? (
                        <p className="my-ontologies__empty">No ontologies have been shared with you yet.</p>
                      ) : (
                        <div className="my-ontologies__list">
                          {sharedOntologies.map((ontology) => (
                            <div
                              key={ontology.id}
                              className="ontology-item"
                              onClick={() => handleOpenOntology(ontology.id)}
                            >
                              <div className="ontology-item__info">
                                <div className="ontology-item__header">
                                  <h4 className="ontology-item__name">{ontology.name}</h4>
                                  {/* Only a published number is a version number.
                                      `ontology.save_label` is a user label, not part
                                      of the version graph, so it is no longer a
                                      fallback here. */}
                                  {ontology.latest_published_version && (
                                    <Tag size="sm" type="blue">v{ontology.latest_published_version}</Tag>
                                  )}
                                  {ontology.status === 'published' ? (
                                    <Tag size="sm" type="green">Published</Tag>
                                  ) : (
                                    <Tag size="sm" type="gray">Draft</Tag>
                                  )}
                                  <Tag size="sm" type="purple">Read only</Tag>
                                </div>
                                {ontology.description && (
                                  <p className="ontology-item__description">{ontology.description}</p>
                                )}
                                <p className="ontology-item__date">
                                  Shared by {ontology.owner_display_name || ontology.owner_email} • {formatDate(ontology.shared_at)}
                                </p>
                              </div>
                              {/*
                                Shared ontologies are read-only for the recipient, so we only
                                expose a "View in editor" inline button (no Share, no Delete).
                              */}
                              <div className="ontology-item__actions" onClick={(e) => e.stopPropagation()}>
                                <IconButton
                                  size="sm"
                                  kind="ghost"
                                  label="View in editor"
                                  align="bottom-right"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleOpenOntology(ontology.id);
                                  }}
                                >
                                  <View />
                                </IconButton>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            )}
          </Tile>

      {/* Share Ontology Modal */}
      {shareTarget && (
        <ShareOntologyModal
          open={!!shareTarget}
          onClose={() => setShareTarget(null)}
          ontologyId={shareTarget.id}
          ontologyName={shareTarget.name}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteTarget}
        onRequestClose={() => setDeleteTarget(null)}
        onRequestSubmit={confirmDeleteOntology}
        modalHeading="Delete ontology?"
        primaryButtonText={isDeleting ? 'Deleting...' : 'Delete'}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={isDeleting}
        danger
        size="xs"
      >
        <p style={{ color: 'var(--cds-text-primary)', fontSize: '0.875rem' }}>
          Are you sure you want to delete <strong>"{deleteTarget?.name}"</strong>?
        </p>
        <p style={{ color: 'var(--cds-text-secondary)', fontSize: '0.875rem', marginTop: '0.75rem' }}>
          This will permanently remove the ontology and all its nodes and relationships. This action cannot be undone.
        </p>
      </Modal>

      {/* Create Ontology Panel */}
      <CreateOntologyPanel
        open={createPanelOpen}
        onClose={() => setCreatePanelOpen(false)}
        onScratch={() => navigate('/editor')}
        onPrompt={() => setShowPromptModal(true)}
        onImportJsonLd={() => importInputRef.current?.click()}
        onKnowledgeBase={() => hasKnowledgeBases ? setShowKBModal(true) : navigate('/knowledge-bases')}
      />
      </Column>
    </Grid>
  );
}

// Made with Bob
